// TTQS — canonical multiplication + division fact spine (Years 1–10).
// Facts are generated deterministically (no hand-authored bank needed).
// A fact FAMILY links 4 retrieval targets: a×b, b×a, p÷a, p÷b — related,
// but each is a SEPARATE retrieval target (per the TTQS spec).

export const TABLES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

// Year a table is introduced (UK NC style). Y5–6 = consolidation, Y7–10 =
// application/fluency inside wider maths — no new tables, everything mixed.
const TABLE_YEAR = { 1: 1, 2: 1, 10: 1, 5: 2, 3: 3, 4: 3, 8: 3, 6: 4, 7: 4, 9: 4, 11: 4, 12: 4 };

// A fact is learnable once BOTH its tables are known → the later of the two.
export function factYear(a, b) {
  return Math.max(TABLE_YEAR[a] || 4, TABLE_YEAR[b] || 4);
}

export function familyId(a, b) {
  const [lo, hi] = a <= b ? [a, b] : [b, a];
  return `f-${lo}x${hi}`;
}

// Build the full multiplication + division spine.
export function buildFacts() {
  const facts = [];
  for (let a = 1; a <= 12; a++) {
    for (let b = 1; b <= 12; b++) {
      const p = a * b;
      const fam = familyId(a, b);
      const yr = factYear(a, b);
      facts.push({
        id: `m-${a}x${b}`, op: "multiplication", a, b, product: p, answer: p,
        familyId: fam, year: yr, table: Math.max(a, b),
        prompt: `${a} × ${b}`, related: [`m-${b}x${a}`, `d-${p}-${a}`, `d-${p}-${b}`],
      });
      facts.push({
        id: `d-${p}-${a}`, op: "division", a: p, b: a, product: b, answer: b,
        familyId: fam, year: yr, table: Math.max(a, b),
        prompt: `${p} ÷ ${a}`, related: [`m-${a}x${b}`, `m-${b}x${a}`],
      });
    }
  }
  // De-dupe (division of squares / commutative products collide by id).
  const seen = new Set();
  return facts.filter((f) => (seen.has(f.id) ? false : seen.add(f.id)));
}

export const ALL_FACTS = buildFacts();
export const FACT_BY_ID = Object.fromEntries(ALL_FACTS.map((f) => [f.id, f]));

// Facts available up to and including a given year (cumulative).
export function factsForYear(year) {
  const y = Math.min(year, 10);
  // Y5+ = all facts (consolidation / application).
  return ALL_FACTS.filter((f) => (y >= 5 ? true : f.year <= y));
}

// Which tables are the *new focus* for a year (for Teach Quest).
export const YEAR_FOCUS = {
  1: [2, 10], 2: [5, 2, 10], 3: [3, 4, 8], 4: [6, 7, 9, 11, 12],
  5: [], 6: [], 7: [], 8: [], 9: [], 10: [],
};

export const YEAR_LABELS = {
  1: "Year 1 · 2s & 10s", 2: "Year 2 · 5s", 3: "Year 3 · 3s, 4s, 8s",
  4: "Year 4 · 6s, 7s, 9s, 11s, 12s", 5: "Year 5 · Mix it all",
  6: "Year 6 · Fluent recall", 7: "Year 7 · Apply", 8: "Year 8 · Apply",
  9: "Year 9 · Apply", 10: "Year 10 · Apply",
};

// Diagnostic distractors for an MCQ — nearby facts reveal misconceptions.
export function distractorsFor(fact) {
  const opts = new Set([fact.answer]);
  const cands = [];
  if (fact.op === "multiplication") {
    cands.push(fact.a * (fact.b + 1), fact.a * (fact.b - 1),
              (fact.a + 1) * fact.b, fact.answer + fact.a, fact.answer - fact.a,
              fact.answer + fact.b);
  } else {
    cands.push(fact.answer + 1, fact.answer - 1, fact.answer + 2, fact.b, // b = the divisor (inverse confusion)
              Math.max(1, fact.answer - 2));
  }
  for (const c of cands) {
    if (c > 0 && !opts.has(c)) opts.add(c);
    if (opts.size >= 4) break;
  }
  let n = fact.answer + 2;
  while (opts.size < 4) { if (n > 0 && !opts.has(n)) opts.add(n); n += 1; }
  return [...opts].sort(() => Math.random() - 0.5);
}

// Classify a wrong answer into a likely misconception.
export function classifyError(fact, given, timedOut) {
  if (timedOut) return "timeout";
  const g = Number(given);
  if (fact.op === "multiplication") {
    if (g === fact.a * (fact.b + 1) || g === fact.a * (fact.b - 1) ||
        g === (fact.a + 1) * fact.b || g === (fact.a - 1) * fact.b) return "nearby-fact";
    if (g === fact.a + fact.b) return "added-not-multiplied";
  } else {
    if (g === fact.b) return "inverse-confusion";     // gave the divisor back
    if (Math.abs(g - fact.answer) === 1) return "off-by-one";
  }
  return "other";
}

export const ERROR_HELP = {
  timeout: "No rush! Let's practise this one a bit more so it comes quickly.",
  "nearby-fact": "So close — that's a neighbour fact. Picture the array to see the difference.",
  "added-not-multiplied": "That's adding! Multiplying means equal groups — try again.",
  "inverse-confusion": "Remember: division undoes multiplication. What times the number gives the total?",
  "off-by-one": "Nearly! Count the groups once more.",
  other: "Good try — let's look at this one together.",
};
